import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Play, Send, ArrowLeft, FileJson, FileText, Cuboid, Shield, ShieldCheck, Download, Layers, Clapperboard, FileBadge, Tag as TagIcon, Sparkles, LibraryBig, FileUp, Users, AlertTriangle, Paperclip, X, ListChecks, BookOpen, Image as ImageIcon, FileSpreadsheet, History, ShieldAlert, ScrollText, FileSignature, Mountain, Leaf, Building2, Smartphone, Map, Box, Truck, Home, Wrench, Grid3x3, GraduationCap, ChevronLeft, ChevronRight, Maximize2, Minimize2 } from "lucide-react";
import api, { API_BASE, formatErr } from "../lib/api";
import { getProjectIndustryLabel } from "../lib/industryLabel";
import AppShell from "../components/AppShell";
import PipelineStepper from "../components/PipelineStepper";
import DiagramCanvas from "../components/DiagramCanvas";
import PfdTabPanel from "../components/diagram/PfdTabPanel";
import PfdDocumentFirstPanel from "../components/diagram/PfdDocumentFirstPanel";
import PidDocumentRegister from "../components/diagram/PidDocumentRegister";
import PidSheetBook from "../components/PidSheetBook";
import Model3DEditor from "../components/Model3DEditor";
import InfraModel3DViewer from "../components/InfraModel3DViewer";
import HighwayMasterPlanPanel from "../components/HighwayMasterPlanPanel"; // eslint-disable-line no-unused-vars
import TagContextMenu from "../components/TagContextMenu";
import TagDetailsPanel from "../components/TagDetailsPanel";
import SymbolLibrary from "../components/SymbolLibrary";
import ImportsTab from "../components/ImportsTab";
import StageWorkspaceTab from "../components/StageWorkspaceTab";
import PdoJsonTab from "../components/PdoJsonTab";
import PhaseHandoffBar from "../components/PhaseHandoffBar";
import CEMatrixEditor from "../components/CEMatrixEditor";
import PlotPlanEditor from "../components/PlotPlanEditor";
import DisciplineHub from "../components/DisciplineHub";
import HazopWorksheet from "../components/HazopWorksheet";
import SilLopaWorksheet from "../components/SilLopaWorksheet";
import LoopsPanel from "../components/LoopsPanel";
import StageGateRail from "../components/StageGateRail";
import StageWalkthrough from "../components/StageWalkthrough";
import VerticalTabNav from "../components/VerticalTabNav";
import useStageGate from "../lib/useStageGate";
import { useFeatureFlags, FeatureFlag } from "../lib/featureFlags";
import CountryComplianceBadges from "../components/CountryComplianceBadges";
import BackToStageBar from "../components/BackToStageBar";
import SendHandoverModal from "../components/SendHandoverModal";
import CustomStandardsPanel from "../components/CustomStandardsPanel";
import DrawingsTab from "../components/DrawingsTab";
import PhotorealisticTab from "../components/PhotorealisticTab";
import DatasheetsTab from "../components/DatasheetsTab";
import VersionsTab from "../components/VersionsTab";
import QraTab from "../components/QraTab";
import AuditLogTab from "../components/AuditLogTab";
import SimulationsTab from "../components/SimulationsTab";
import Tier3Tab from "../components/Tier3Tab";
import ProjectReadinessDashboard from "../components/masterplan/ProjectReadinessDashboard";
import DesignReviewWorkspace from "../components/DesignReviewWorkspace";
import EngineeringBackbone from "../components/EngineeringBackbone";
import CostDashboardWidget from "../components/CostDashboardWidget";
import OverviewFishbone from "../components/fishbone/OverviewFishbone";
import CoachCard from "../components/CoachCard";
import GlobalStageProgressBar from "../components/GlobalStageProgressBar";

// Master tab catalog — each tab declares which domain(s) it's relevant for.
// `domains: null` means "always visible". The Workspace filters this list
// by the project's domain so users only see tabs relevant to their work.
//
// NOTE (Wave V — UI restructure): the "Stages" entry is removed from this
// list because the stage-gate rail is now globally visible on the left
// column. Selecting any stage from the rail switches the content area to
// the stage workspace pane.
const ALL_TABS = [
  { key: "overview", label: "Overview", icon: Layers, domains: null },

  // ── Process-plant only
  // NOTE: Process Simulation, PFD, P&ID and Datasheets are rendered in-place
  // inside their owning stage workspaces (B.1, B.2, B.3, B.4). They are
  // intentionally NOT exposed here. Haz. Area and QRA remain global Views-Panel
  // tabs so they stay accessible without first opening B.6 Safety Studies.
  { key: "pdo", label: "PDO JSON", icon: FileJson, domains: ["process_plant"] },
  { key: "hazarea", label: "Haz. Area", icon: ShieldCheck, domains: ["process_plant"] },
  { key: "qra", label: "QRA", icon: ShieldAlert, domains: ["process_plant"] },
  { key: "ce_matrix", label: "C&E Matrix", icon: ShieldCheck, domains: ["process_plant"] },
  { key: "plot_plan", label: "Plot Plan", icon: Grid3x3, domains: ["process_plant"] },
  { key: "hazop", label: "HAZOP", icon: ShieldAlert, domains: ["process_plant"] },
  { key: "sil_lopa", label: "SIL / LOPA", icon: ShieldAlert, domains: ["process_plant"] },
  { key: "disciplines", label: "Disciplines", icon: Layers, domains: ["process_plant"] },
  { key: "loops", label: "Loops (F.4)", icon: ListChecks, domains: ["process_plant"] },

  // ── Infrastructure only
  { key: "infra-sim", label: "Infra Simulation", icon: Truck, domains: ["infrastructure"] },

  // ── Real-Estate only
  { key: "readiness", label: "Readiness", icon: ShieldCheck, domains: ["real_estate"] },
  { key: "master-plan", label: "A0 · Master Plan", icon: Map, domains: ["real_estate"] },
  // Wave DR — Design Review (independent review + optimize workflow;
  // works on ANY drawings uploaded, not just A0-generated ones).
  { key: "design-review", label: "Design Review", icon: Shield, domains: ["real_estate"] },
  // Sprint 3 Final — highway master plan moved INSIDE the A.5.1 stage
  // gate (HighwayCorridorPlanPanel). Sidebar tab removed per the
  // "no sidebar tabs for deliverables" architectural rule.
  // (was: { key: "highway-master-plan", ... domains: ["infrastructure"] })
  { key: "re-sim", label: "Building Simulation", icon: Home, domains: ["real_estate"] },
  { key: "green", label: "Green Building", icon: Leaf, domains: ["real_estate"] },
  { key: "compliance", label: "Code Compliance", icon: ShieldCheck, domains: ["real_estate"] },

  // ── Infra + Real-Estate (geo, survey, AR/VR are needed by both)
  { key: "geotech", label: "Geotech & Foundation", icon: Mountain, domains: ["infrastructure", "real_estate"] },
  { key: "survey", label: "Survey & GIS", icon: Map, domains: ["infrastructure", "real_estate"] },
  { key: "arvr", label: "AR / VR / 360°", icon: Smartphone, domains: ["infrastructure", "real_estate"] },

  // ── BIM execution plan: applicable to all (universal ISO 19650 standard)
  { key: "bep", label: "BIM Plan", icon: Building2, domains: null },

  // ── Universal: Project Deliverables (BOQ / Schedule / Tender / Calc / BBS)
  { key: "deliverables", label: "Deliverables", icon: FileSpreadsheet, domains: null },

  // ── Shared: drawings, datasheets, 3D model, clashes, versions, etc.
  { key: "drawings", label: "Drawings", icon: ImageIcon, domains: null },
  { key: "datasheets", label: "Datasheets", icon: FileSpreadsheet, domains: ["real_estate"] },
  { key: "model", label: "3D Model", icon: Cuboid, domains: null },
  { key: "photorealistic", label: "Photoreal", icon: Cuboid, domains: ["infrastructure"] },
  { key: "clashes", label: "Clashes", icon: AlertTriangle, domains: ["process_plant", "real_estate"] },
  { key: "versions", label: "Versions", icon: History, domains: null },
  { key: "validation", label: "Validation", icon: ShieldCheck, domains: ["process_plant"] },
  { key: "audit", label: "Audit", icon: ScrollText, domains: null },
  { key: "standards", label: "Standards", icon: BookOpen, domains: null },
  { key: "tags", label: "Tag DB", icon: TagIcon, domains: ["process_plant"] },
  { key: "symbols", label: "Symbols", icon: LibraryBig, domains: ["process_plant"] },
  { key: "engineering-backbone", label: "Engg Backbone", icon: Wrench, domains: null },
  { key: "imports", label: "Imports", icon: FileUp, domains: null },
  { key: "collab", label: "Team", icon: Users, domains: null },
  { key: "files", label: "Files", icon: Download, domains: null },
];

function tabsForDomain(domain) {
  return ALL_TABS.filter((t) => !t.domains || t.domains.includes(domain || "process_plant"));
}

const EXAMPLES = [
  "Design a crude oil desalter unit: feed tank → mix valve → electrostatic desalter vessel → desalted crude storage tank. Capacity 100 m3/h, 50 °C.",
  "Biodiesel plant 5 t/day: oil feed tank, methanol tank, transesterification reactor with KOH catalyst, settling separator, methanol recovery column, glycerol drain tank, biodiesel product tank.",
  "Municipal wastewater treatment train: screening, grit chamber, primary clarifier, aeration basin with blowers, secondary clarifier, chlorination, sludge thickener. Design flow 10 MGD.",
  "Small LNG liquefaction train: feed-gas compressor, acid-gas removal column, dehydration, precooling exchanger, cold box with MR cycle, LNG rundown tank. Capacity 1 mtpa.",
];

export default function Workspace() {
  const { id: projectId } = useParams();
  const nav = useNavigate();
  const [project, setProject] = useState(null);
  // Wave AG — phase visibility (fetched after project loads)
  const [phaseVisibility, setPhaseVisibility] = useState(null);
  // Phase 19m: project-level drawings (populated by stage-gate B.2/B.3)
  const [projectPfd, setProjectPfd] = useState(null); // {svg, pdo, updated_at, source, has_drawing}
  const [projectPid, setProjectPid] = useState(null); // {sheets, index_svg, svg, ...}
  const [projectModel, setProjectModel] = useState(null); // {gltf_url, has_model, ...}
  // Phase 19p — Send to Recipient modal
  const [sendModal, setSendModal] = useState(null); // null | { scope, stageCode, label }
  const [runs, setRuns] = useState([]);
  const [current, setCurrent] = useState(null); // full run object
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // Honor ?tab=design-review deep link (RG10)
  const [tab, setTab] = useState(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const t = sp.get("tab");
      return t || "stages";
    } catch { return "stages"; }
  });
  const [messages, setMessages] = useState([]);
  const [ctxMenu, setCtxMenu] = useState(null); // {tag, x, y}
  const [openTag, setOpenTag] = useState(null); // tag string
  const [tags, setTags] = useState([]);
  const [focus3D, setFocus3D] = useState(null); // {tag, x, y, z, label}
  const [revalidating, setRevalidating] = useState(false);
  const [thoughts, setThoughts] = useState([]); // SSE thought stream

  // Wave V — stage-gate state shared between the global rail and the
  // stage-workspace pane. railCollapsed/navCollapsed control the new
  // collapsible left columns that replaced the horizontal scroll bar.
  const gate = useStageGate(projectId);
  const featureFlags = useFeatureFlags();
  // Tell the FF provider to re-resolve flags scoped to this project so
  // country-specific gating (Green Mark / CDM / RERA / NCC J / etc.) works.
  useEffect(() => {
    if (projectId) featureFlags.setProjectId(projectId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);
  const [stageCode, setStageCode] = useState(null);
  const [railCollapsed, setRailCollapsed] = useState(() => {
    // CH.3 — default everything collapsed for a focused first-load.
    try { return sessionStorage.getItem("rachana.railCollapsed") !== "0"; }
    catch { return true; }
  });
  const [navCollapsed, setNavCollapsed] = useState(true);
  const [walkthroughOpen, setWalkthroughOpen] = useState(false);
  const [actionBarCollapsed, setActionBarCollapsed] = useState(() => {
    // CH.3 — default to collapsed so first-load shows a focused workspace.
    try { return sessionStorage.getItem("rachana.actionBarCollapsed") !== "0"; }
    catch { return true; }
  });
  // CH.3 — full-screen / minimize the stage workspace. Hides the Stage Gate
  // Rail so the workspace takes the entire viewport width.
  const [workspaceFullscreen, setWorkspaceFullscreen] = useState(false);
  // CH.3 — when fullscreen is ON, also hide the global AppShell header
  // (Capexara Process Industry logo + Projects + Cost + theme + profile) via a body class
  // so the workspace really fills the entire viewport.
  useEffect(() => {
    if (workspaceFullscreen) {
      document.body.classList.add("ws-fullscreen");
    } else {
      document.body.classList.remove("ws-fullscreen");
    }
    return () => document.body.classList.remove("ws-fullscreen");
  }, [workspaceFullscreen]);
  const [healthScore, setHealthScore] = useState(null);
  const [countryCfg, setCountryCfg] = useState(null);

  const loadProject = useCallback(async () => {
    try {
      const [p, r, m, pfdR, pidR, modelR] = await Promise.all([
        api.get(`/projects/${projectId}`),
        api.get(`/projects/${projectId}/runs`),
        api.get(`/projects/${projectId}/messages`),
        api.get(`/projects/${projectId}/pfd`).catch(() => ({ data: { svg: "", has_drawing: false } })),
        api.get(`/projects/${projectId}/pid`).catch(() => ({ data: { sheets: [], svg: "", index_svg: "", has_drawing: false } })),
        api.get(`/projects/${projectId}/model3d`).catch(() => ({ data: { gltf_url: "", has_model: false } })),
      ]);
      setProject(p.data);
      setRuns(r.data);
      setMessages(m.data);
      setProjectPfd(pfdR.data);
      setProjectPid(pidR.data);
      setProjectModel(modelR.data);
      // Wave AG — fetch phase visibility (non-fatal)
      try {
        const pv = await api.get(`/projects/${projectId}/phase/visibility`);
        setPhaseVisibility(pv.data);
      } catch { /* visibility is optional */ }
      if (r.data.length && !current) {
        const { data } = await api.get(`/projects/${projectId}/runs/${r.data[0].id}`);
        setCurrent(data);
      }
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    }
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadProject(); }, [loadProject]);

  // Fetch project health score (non-fatal; refreshes when project changes)
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    api.get(`/projects/${projectId}/health-score`)
      .then((r) => { if (!cancelled) setHealthScore(r.data); })
      .catch(() => { if (!cancelled) setHealthScore(null); });
    return () => { cancelled = true; };
  }, [projectId, current?.status]);

  // Fetch country config based on project's country (defaults to IN)
  useEffect(() => {
    const code = project?.country || project?.location_country
                  || project?.pdo?.design_basis?.country || "IN";
    let cancelled = false;
    api.get(`/config/countries/${code}`)
      .then((r) => { if (!cancelled) setCountryCfg(r.data); })
      .catch(() => { if (!cancelled) setCountryCfg(null); });
    return () => { cancelled = true; };
  }, [project?.country, project?.location_country,
      project?.pdo?.design_basis?.country]);

  // Poll when a run is active
  useEffect(() => {
    if (!current) return;
    if (current.status === "complete" || current.status === "failed") return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/projects/${projectId}/runs/${current.id}`);
        setCurrent(data);
      } catch {}
    }, 2000);
    return () => clearInterval(t);
  }, [current?.id, current?.status, projectId]); // eslint-disable-line

  // SSE: live thought stream of the running pipeline (Wave 5)
  useEffect(() => {
    if (!current?.id) {
      setThoughts([]);
      return;
    }
    // Always reset the stream when the run changes
    setThoughts([]);
    const url = `${API_BASE}/projects/${projectId}/runs/${current.id}/stream`;
    const es = new EventSource(url, { withCredentials: true });
    const onAny = (evt) => {
      try {
        const data = JSON.parse(evt.data);
        if (data.kind === "done" && data.message === "__end__") {
          es.close();
          return;
        }
        setThoughts((prev) => [...prev, data]);
      } catch {
        /* ignore malformed frames */
      }
    };
    // One listener per event kind we emit
    ["info", "layer", "validation", "warn", "error", "done"].forEach((k) => es.addEventListener(k, onAny));
    es.addEventListener("message", onAny); // fallback for un-kinded frames
    es.onerror = () => {
      // Browser will retry; nothing for us to do. Close when run is done.
    };
    return () => es.close();
  }, [current?.id, projectId]);

  // Phase 19l: chat column removed — auto-scroll effects deleted.
  // (They were causing the page to jump on every thought/message update
  //  because the refs now point at nothing, but `scrollIntoView` still
  //  fires and triggers browsers to scroll the nearest scroll ancestor.)

  const runPipeline = async (overridePrompt) => {
    const toRun = (overridePrompt || prompt || "").trim();
    if (!toRun || busy) return;
    setBusy(true); setErr("");
    let runCreated = false;
    let createdRunId = null;
    try {
      // Step 1 — create the run. THIS is the only step where a hard error
      // should bubble to the user, because nothing else has happened yet.
      const { data } = await api.post(`/projects/${projectId}/runs`, { prompt: toRun });
      runCreated = true;
      createdRunId = data.run_id;
      if (!overridePrompt) setPrompt("");

      // Step 2 — best-effort UI hydration. If the backend is busy serving
      // the long-running pipeline call, these can transiently 502/timeout.
      // Don't fail the user-facing flow — the WebSocket / polling layer
      // will catch up shortly.
      try {
        const m = await api.get(`/projects/${projectId}/messages`);
        setMessages(m.data);
      } catch (e2) { console.warn("messages fetch deferred:", e2?.message); }

      try {
        const r = await api.get(`/projects/${projectId}/runs/${createdRunId}`);
        setCurrent(r.data);
      } catch (e2) {
        console.warn("run fetch deferred:", e2?.message);
        // Synthesize a placeholder so the chat shows the engine is working
        setCurrent({ id: createdRunId, status: "running", current_layer: 1, layers: {} });
      }

      try {
        const allRuns = await api.get(`/projects/${projectId}/runs`);
        setRuns(allRuns.data);
      } catch (e2) { console.warn("runs list fetch deferred:", e2?.message); }

      setTab("stages");
    } catch (e) {
      // Real failure (run could not be created). Decode like Login does
      // so the user sees a useful message even on 502/network failures.
      if (!runCreated) {
        const detail = e.response?.data?.detail;
        if (detail) setErr(formatErr(detail));
        else if (e.response) {
          const code = e.response.status;
          if (code === 502 || code === 503 || code === 504) {
            setErr("Server is busy generating another design. Please wait a moment and try again.");
          } else {
            setErr(`Could not start the run (HTTP ${code}). Please try again.`);
          }
        } else {
          setErr("Could not reach the server. Check your internet connection and try again.");
        }
      }
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault(); runPipeline();
    }
  };

  // Phase 19l: design-brief file upload removed along with the chat column.
  // Briefs are now ingested as stage attachments via StagePanel.

  const pdo = current?.pdo_enriched || current?.pdo || null;
  const gltfUrl = (() => {
    // Phase 19o — prefer project-level Stage-J glTF, fall back to chat run-level
    if (projectModel?.gltf_url) return `${process.env.REACT_APP_BACKEND_URL}${projectModel.gltf_url}`;
    if (current?.gltf_url) return `${process.env.REACT_APP_BACKEND_URL}${current.gltf_url}`;
    return "";
  })();

  const loadTags = useCallback(async () => {
    if (!projectId) return;
    try {
      const { data } = await api.get(`/projects/${projectId}/tags`);
      setTags(data);
    } catch {}
  }, [projectId]);

  useEffect(() => { loadTags(); }, [loadTags, current?.status]);

  const onTagContextMenu = useCallback(({ tag, x, y }) => setCtxMenu({ tag, x, y }), []);
  const openTagDetails = (tag) => { setCtxMenu(null); setOpenTag(tag); };
  const closeTagDetails = () => { setOpenTag(null); loadTags(); };

  const locateInThreeD = (tag) => {
    const pos = (current?.equipment_positions || {})[tag];
    if (!pos) return;
    setFocus3D({ tag, x: pos.center.x, y: pos.center.y, z: pos.center.z, label: pos.label });
    setTab("model");
    setOpenTag(null);
  };

  /** Jump to 3D view with the camera centred on a specific clash marker.
   *  Clash.location is expressed in mm in v4 — we convert to metres to match
   *  the glTF coordinate system (which uses metres internally). */
  const focusOnClash = (clash) => {
    if (!clash?.location || clash.location.length < 3) return;
    const [xm, ym, zm] = clash.location;
    setFocus3D({
      tag: clash.id,
      x: xm / 1000.0,
      y: ym / 1000.0,
      z: zm / 1000.0,
      label: `${clash.id} · ${clash.object_1} ↔ ${clash.object_2}`,
    });
    setTab("model");
  };

  // Wave V.2 — interactive views that used to live in the global views nav
  // are now rendered inside their owning stage workspace. Each stage code
  // maps to a list of extra sub-tabs (each with its own render fn so all the
  // closure state — projectPfd, projectPid, current, tags, etc. — stays here).
  const stageExtraTabs = {
    "B.2": [
      {
        key: "pfd",
        label: "PFD",
        Icon: FileText,
        render: () => (
          <PfdDocumentFirstPanel projectId={projectId}/>
        ),
      },
    ],
    "B.3": [
      {
        key: "pid",
        label: "P&ID",
        Icon: FileBadge,
        render: () => (
          <PidDocumentRegister projectId={projectId}/>
        ),
      },
    ],
    "B.4": [
      {
        key: "datasheets",
        label: "Datasheets",
        Icon: FileSpreadsheet,
        render: () => <DatasheetsTab projectId={projectId} />,
      },
    ],
  };


  return (
    <AppShell
      actions={
        <>
          <Link to="/projects" className="btn btn-ghost" data-testid="ws-back"><ArrowLeft size={14}/> Projects</Link>
        </>
      }
    >
      <div style={{ display: "flex", minHeight: 0, flexDirection: "column",
                          height: workspaceFullscreen ? "100vh" : "calc(100vh - 73px)",
                          overflow: "hidden" }}>
        {/* Wave AG — Phase banner with handoff controls (top of workspace).
            CH.3 — hidden in fullscreen mode to free vertical space. */}
        {!workspaceFullscreen && project?.phase && (
          <PhaseHandoffBar
            project={project}
            onPhaseChanged={async (newPhase) => {
              setProject((p) => ({ ...(p || {}), phase: newPhase }));
              try {
                const pv = await api.get(`/projects/${projectId}/phase/visibility`);
                setPhaseVisibility(pv.data);
              } catch {/* ignore */}
            }}
          />
        )}
        {/* Wave BD — Global progress strip surfaces in-flight stage runs and PFD queue.
            CH.3 — hidden in fullscreen mode to free vertical space. */}
        {!workspaceFullscreen && (
        <GlobalStageProgressBar
          projectId={projectId}
          onJumpStage={(code) => { setStageCode(code); setTab("stages"); }}
        />
        )}
        <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
        {/* Wave V — Global stage-gate rail (always visible across all tabs).
            CH.3 — hidden in fullscreen mode so the workspace takes 100% width. */}
        {!workspaceFullscreen && (
        <StageGateRail
          {...gate}
          projectId={projectId}
          activeCode={stageCode}
          onPick={(code) => { setStageCode(code); setTab("stages"); }}
          collapsed={railCollapsed}
          onToggle={() => setRailCollapsed((v) => {
            const n = !v;
            try { sessionStorage.setItem("rachana.railCollapsed", n ? "1" : "0"); } catch {}
            return n;
          })}
          phaseVisibility={phaseVisibility}
          subTypeLabel={getProjectIndustryLabel(project, { withSector: false })}
          projectName={project?.name}
          domain={project?.domain}
          subIndustry={project?.sub_industry || project?.sub_industry_key
                          || project?.sub_type || project?.industry}
        />
        )}

        {/* Wave V — Vertical collapsible tab nav (replaces horizontal scrollbar).
         *  For Real-Estate projects we PIN the A0 Master Plan entry as a
         *  prominent CTA above the regular tab list so users always see it.
         *  We filter `master-plan` out of the regular list to avoid
         *  duplication.
         */}
        {(() => {
          const allTabs = tabsForDomain(project?.domain);
          const isRE = (project?.domain === "real_estate");
          const regularTabs = isRE
            ? allTabs.filter((t) => t.key !== "master-plan")
            : allTabs;
          return (
            <div style={{ display: "flex", flexDirection: "column",
                          flexShrink: 0,
                          background: "var(--pf-surface-2)" }}
                 data-testid="views-panel">
              {isRE && (
                <button
                  type="button"
                  data-testid="vtab-master-plan"
                  onClick={() => {
                    // Switch to Stage workspace and activate A.5.1 (the
                    // Master Plan output deliverable). The stage rail
                    // listens for "rachana.stage.activate" events.
                    setStageCode("A.5.1");
                    setTab("stages");
                    try {
                      window.dispatchEvent(new CustomEvent(
                        "rachana.stage.activate",
                        { detail: { code: "A.5.1" } },
                      ));
                    } catch (e) {}
                  }}
                  title="A0 · Master Plan — open Stage A.5 (output) in the stage gate"
                  style={{
                    display: "flex", alignItems: "center",
                    gap: navCollapsed ? 0 : 10,
                    justifyContent: navCollapsed ? "center" : "flex-start",
                    width: navCollapsed ? 48 : 200,
                    padding: navCollapsed ? "12px 8px" : "12px 14px",
                    margin: navCollapsed ? "8px 4px" : "8px",
                    border: "1px solid #0e7490",
                    borderRadius: 6,
                    background: "linear-gradient(135deg, #0e7490 0%, #06b6d4 100%)",
                    color: "#ecfeff",
                    cursor: "pointer",
                    fontWeight: 700,
                    fontSize: 12,
                    boxShadow: "0 0 0 1px rgba(34, 211, 238, 0.25)",
                  }}>
                  <Map size={16} style={{ flexShrink: 0 }} />
                  {!navCollapsed && (
                    <span style={{ display: "flex", flexDirection: "column",
                                   alignItems: "flex-start", lineHeight: 1.2 }}>
                      <span style={{ fontSize: 9, letterSpacing: 1, opacity: 0.85 }}>PHASE A0 · GIS</span>
                      <span style={{ fontSize: 12, fontWeight: 700 }}>Master Plan</span>
                    </span>
                  )}
                </button>
              )}
              <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
                <VerticalTabNav
                  tabs={regularTabs}
                  active={tab === "stages" ? null : tab}
                  onPick={(k) => {
                    if (k === "master-plan") {
                      nav(`/projects/${projectId}/master-plan`);
                      return;
                    }
                    setTab(k);
                  }}
                  collapsed={navCollapsed}
                  onToggle={() => setNavCollapsed((v) => !v)}
                  header={<span className="pf-overline" style={{ margin: 0 }}>VIEWS</span>}
                />
              </div>
            </div>
          );
        })()}

        {/* RIGHT: TAB CONTENT */}
        <section style={{ display: "flex", flexDirection: "column",
                                  minHeight: 0, minWidth: 0, flex: 1 }}>
          <div style={{ padding: "10px 16px",
                              borderBottom: "1px solid var(--pf-border)",
                              display: "flex", alignItems: "center", gap: 8,
                              background: "var(--pf-surface)" }}>
            {tab === "stages" ? (
              <span style={{ fontSize: 12, color: "var(--pf-text-muted)" }}>
                Stage workspace · {stageCode || (gate.data?.stages || [])[0]?.code || "—"}
              </span>
            ) : (
              <span style={{ fontSize: 12, color: "var(--pf-text-muted)" }}>
                {(tabsForDomain(project?.domain).find((t) => t.key === tab) || {}).label || tab}
              </span>
            )}
            <div style={{ flex: 1 }} />
            {/* CH.3 — Fullscreen / Minimize the stage workspace (hides the
                left Stage Gate Rail to give the workspace the entire screen). */}
            <button type="button"
                data-testid="workspace-fullscreen-toggle"
                onClick={() => setWorkspaceFullscreen((v) => !v)}
                className="btn btn-ghost"
                title={workspaceFullscreen ? "Restore default layout" : "Full-screen workspace"}
                style={{ fontSize: 12, padding: "4px 8px",
                            border: "1px solid var(--pf-border)",
                            color: workspaceFullscreen ? "var(--pf-spark)" : "var(--pf-text-muted)" }}>
              {workspaceFullscreen
                ? <><Minimize2 size={13}/> Minimize</>
                : <><Maximize2 size={13}/> Fullscreen</>}
            </button>
            {/* Horizontal action bar — collapsible to save space on small screens
                or when user wants to focus on the stage content. */}
            <button type="button"
                data-testid="action-bar-toggle"
                onClick={() => setActionBarCollapsed((v) => {
                  const n = !v;
                  try { sessionStorage.setItem("rachana.actionBarCollapsed", n ? "1" : "0"); } catch {}
                  return n;
                })}
                className="btn btn-ghost"
                title={actionBarCollapsed ? "Show project actions" : "Hide project actions"}
                style={{ fontSize: 12, padding: "4px 8px",
                            border: "1px solid var(--pf-border)",
                            color: "var(--pf-text-muted)" }}>
              {actionBarCollapsed
                ? <><ChevronLeft size={13}/> Actions</>
                : <><ChevronRight size={13}/> Hide</>}
            </button>
            <div data-testid="workspace-action-bar"
                style={{ display: "flex", alignItems: "center", gap: 8,
                            maxWidth: actionBarCollapsed ? 0 : 2000,
                            overflow: "hidden",
                            opacity: actionBarCollapsed ? 0 : 1,
                            pointerEvents: actionBarCollapsed ? "none" : "auto",
                            transition: "max-width 0.25s ease, opacity 0.18s ease" }}>
            {/* Stage walk-through — guided learning tour through stage codes */}
            {tab === "stages" && (
              <button type="button"
                  data-testid="walkthrough-toggle-btn"
                  className="btn btn-ghost"
                  style={{ fontSize: 12 }}
                  onClick={() => setWalkthroughOpen((v) => !v)}
                  title="Stage walk-through — auto-step through every stage to learn the design sequence">
                <GraduationCap size={13} />
                {walkthroughOpen ? "Hide tour" : "Walk-through"}
              </button>
            )}
            {/* Country pill — design-code basis */}
            {countryCfg && (() => {
              const FLAGS = { IN: "🇮🇳", US: "🇺🇸", ME: "🌍",
                              UK: "🇬🇧", AU: "🇦🇺", SG: "🇸🇬" };
              const flag = FLAGS[countryCfg.country_code] || "🏳️";
              const codes = countryCfg.design_codes || {};
              const tooltip = (
                `Country: ${countryCfg.country_name}\n` +
                `Concrete: ${codes.structural_concrete || "—"}\n` +
                `Steel: ${codes.structural_steel || "—"}\n` +
                `Seismic: ${(countryCfg.seismic || {}).code || "—"}\n` +
                `Wind: ${(countryCfg.wind || {}).code || "—"}\n` +
                `Currency: ${countryCfg.currency} ${countryCfg.currency_symbol}`
              );
              const headlineCode = codes.structural_concrete
                || (countryCfg.seismic || {}).code || "";
              const shortCode = headlineCode.split(":")[0]
                .replace(/\(.*\)/, "").trim().slice(0, 16);
              return (
                <div
                  data-testid="workspace-country-pill"
                  title={tooltip}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    padding: "4px 10px", borderRadius: 999,
                    border: "1.5px solid #3aa0ff",
                    background: "rgba(58,160,255,0.1)", fontSize: 11,
                    cursor: "default",
                  }}
                >
                  <span style={{ fontSize: 14 }}>{flag}</span>
                  <span style={{ color: "#3aa0ff", fontWeight: 700,
                                  fontFamily: "JetBrains Mono",
                                  letterSpacing: 0.5 }}>
                    {countryCfg.country_code}
                  </span>
                  <span style={{ color: "var(--pf-text-muted)",
                                  fontSize: 10 }}>·</span>
                  <span style={{ color: "var(--pf-text-muted)",
                                  fontSize: 10 }}>
                    {shortCode}
                  </span>
                </div>
              );
            })()}
            {/* Country-specific compliance badges (driven by feature flags
                from /api/me/features). Each pill renders only when its flag
                is true AND its `domain` matches the project's domain — so
                e.g. RERA shows only on real_estate projects in IN. */}
            <CountryComplianceBadges projectDomain={project?.domain} />
            {/* Brownfield Mode toggle — only visible when the platform
                feature flag is enabled. Currently OFF for all 6 countries
                (deferred per product roadmap), so this button renders only
                after platform.brownfield_retrofit flips true. */}
            <FeatureFlag flag="platform.brownfield_retrofit">
              <button
                type="button"
                className="btn btn-ghost"
                data-testid="workspace-brownfield-mode"
                style={{ fontSize: 12,
                          border: "1.5px solid #f59e0b",
                          color: "#f59e0b" }}
                title="Switch this project to Brownfield (retrofit / code-of-record) mode"
              >
                Brownfield Mode
              </button>
            </FeatureFlag>
            {/* Project Health Score — glowing pill badge */}
            {healthScore && (
              <div
                data-testid="workspace-health-badge"
                title={`Deliverables ${healthScore.components?.deliverable_progress ?? "—"}% · Issuance ${healthScore.components?.issuance_progress ?? "—"}% · Comments ${healthScore.components?.comments_health ?? "—"}% · Clashes ${healthScore.components?.clash_health ?? "—"}% · CAPEX ${healthScore.components?.capex_confidence ?? "—"}%`}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  padding: "4px 10px", borderRadius: 999,
                  border: `1.5px solid ${healthScore.color || "#9CA3AF"}`,
                  background: "rgba(0,0,0,0.25)", fontSize: 11,
                }}
              >
                <span style={{
                  width: 8, height: 8, borderRadius: "50%",
                  background: healthScore.color || "#9CA3AF",
                  boxShadow: `0 0 6px ${healthScore.color || "#9CA3AF"}`,
                }} />
                <span style={{ color: healthScore.color || "#9CA3AF",
                               fontWeight: 700, fontFamily: "JetBrains Mono" }}>
                  {healthScore.score}/100
                </span>
                <span style={{ color: "var(--pf-text-muted)", fontWeight: 600,
                               letterSpacing: 0.4, textTransform: "uppercase" }}>
                  {healthScore.verdict}
                </span>
              </div>
            )}
            {/* Documents Register — single-table EDMS view of every deliverable */}
            <Link
              to={`/projects/${projectId}/documents`}
              className="btn btn-ghost"
              data-testid="project-documents-register"
              style={{ fontSize: 12 }}
              title="Documents Register · doc number, revision, status, transmittals"
            >
              <FileText size={13}/> Documents
            </Link>
            {/* Engineering Package — production XLSX + PDF bundle */}
            <a
              className="btn btn-ghost"
              href={`${(process.env.REACT_APP_BACKEND_URL || "")}/api/projects/${projectId}/engineering-package`}
              data-testid="project-engineering-package"
              style={{ fontSize: 12 }}
              title="Download production engineering package (XLSX + PDF + MD + JSON per deliverable)"
            >
              <Download size={13}/> Engineering Package
            </a>
            {/* Phase 19p — project-level handover bundle (visible from any tab) */}
            <a
              className="btn btn-ghost"
              href={`${(process.env.REACT_APP_BACKEND_URL || "")}/api/projects/${projectId}/handover.zip`}
              data-testid="project-handover-zip"
              style={{ fontSize: 12 }}
              title="Download every deliverable across all stages as one ZIP"
            >
              <Download size={13}/> Project ZIP
            </a>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => setSendModal({ scope: "project", label: `the entire ${project?.name || "project"}` })}
              data-testid="project-send-recipient"
              style={{ fontSize: 12 }}
              title="Email the project handover bundle to a recipient"
            >
              <Send size={13}/> Send project
            </button>
            {runs.length > 0 && (
              <select
                className="pf-input"
                style={{ width: "auto", margin: 0, fontFamily: "JetBrains Mono", fontSize: 12 }}
                value={current?.id || ""}
                onChange={async (e) => {
                  const { data } = await api.get(`/projects/${projectId}/runs/${e.target.value}`);
                  setCurrent(data);
                }}
                data-testid="run-selector"
              >
                {runs.map((r) => (
                  <option key={r.id} value={r.id}>{`RUN ${r.id.slice(0, 8)} · ${r.status}`}</option>
                ))}
              </select>
            )}
            </div>{/* end workspace-action-bar */}
          </div>

          {err && (
            <div className="pf-sev pf-sev-error" style={{ display: "block", margin: "8px 16px 0", padding: "8px 12px" }} data-testid="ws-error">
              {err}
            </div>
          )}

          <div style={{ flex: 1, padding: 16, overflow: "auto" }}>
            {tab === "overview" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <CostDashboardWidget
                  projectId={projectId}
                  onOpenCost={() => {
                    // Open the cost-estimation panel in the engineering page
                    try { window.location.assign(`/projects/${projectId}/engineering/cost-engineering/cost-estimation`); } catch {}
                  }}
                />
                <OverviewFishbone
                  projectId={projectId}
                  onOpenStage={(stageCode) => {
                    setStageCode(stageCode);
                    try { sessionStorage.setItem("rachana.stage.activate", stageCode); } catch {}
                    setTab("stages");
                  }}
                  onOpenView={(viewId) => {
                    const map = {
                      pdo_json: "pdo", tag_db: "tags", haz_area: "hazarea", qra: "qra",
                      model_3d: "model", clashes: "clashes", drawings: "drawings",
                      deliverables: "deliverables", validation: "validation",
                      standards: "standards", versions: "versions", audit: "audit",
                      bep: "bep", symbols: "symbols", engg_backbone: "engineering-backbone",
                      imports: "imports", team: "collab", files: "files",
                    };
                    const target = map[viewId];
                    if (target) setTab(target);
                  }}
                />
                <OverviewTab run={current} pdo={pdo} onOpenTag={openTagDetails} />
              </div>
            )}
            {tab === "overview" && (
              <CoachCard
                projectId={projectId}
                onOpenStage={(stageCode) => {
                  setStageCode(stageCode);
                  try { sessionStorage.setItem("rachana.stage.activate", stageCode); } catch {}
                  setTab("stages");
                }}
              />
            )}
            {tab === "stages" && (
              <div style={{ height: "calc(100vh - 220px)", marginTop: -16, marginLeft: -16, marginRight: -16, marginBottom: -16 }}>
                <StageWorkspaceTab
                  projectId={projectId}
                  onOpenTab={setTab}
                  onSendHandover={setSendModal}
                  hideRail
                  externalActiveCode={stageCode}
                  onActiveCodeChange={setStageCode}
                  stageExtraTabs={stageExtraTabs}
                  domain={project?.domain}
                  subType={project?.sub_type || project?.sub_industry_key
                              || project?.industry}
                  subTypeLabel={getProjectIndustryLabel(project, { withSector: false })}
                  projectName={project?.name}
                />
              </div>
            )}
            {tab === "infra-sim" && <SimulationsTab projectId={projectId} mode="infra" domain="infrastructure" />}
            {tab === "re-sim" && <SimulationsTab projectId={projectId} mode="re" domain="real_estate" />}
            {tab === "readiness" && <ProjectReadinessDashboard projectId={projectId} />}
            {tab === "design-review" && <DesignReviewWorkspace projectId={projectId} project={project} />}
            {tab === "deliverables" && <SimulationsTab projectId={projectId} mode="deliverables" domain={project?.domain || "real_estate"} />}
            {tab === "survey" && <SimulationsTab projectId={projectId} mode="survey" domain={project?.domain || "infrastructure"} />}
            {tab === "geotech" && <Tier3Tab projectId={projectId} section="geotech" />}
            {tab === "green" && <Tier3Tab projectId={projectId} section="green" />}
            {tab === "bep" && <Tier3Tab projectId={projectId} section="bep" />}
            {tab === "arvr" && <Tier3Tab projectId={projectId} section="arvr" />}
            {tab === "compliance" && <Tier3Tab projectId={projectId} section="compliance" />}
            {tab === "engineering-backbone" && <EngineeringBackbone projectId={projectId} />}
            {tab === "drawings" && <DrawingsTab projectId={projectId} />}
            {tab === "datasheets" && <DatasheetsTab projectId={projectId} />}
            {tab === "versions" && <VersionsTab projectId={projectId} />}
            {tab === "audit" && <AuditLogTab projectId={projectId} />}
            {tab === "standards" && <CustomStandardsPanel projectId={projectId} project={project} onChange={() => api.get(`/projects/${projectId}`).then(({ data }) => setProject(data))} />}
            {tab === "pdo" && <PdoJsonTab projectId={projectId} />}
            {tab === "hazarea" && <HazareaTab projectId={projectId} runId={current?.id} />}
            {tab === "qra" && <QraTab projectId={projectId} />}
            {tab === "photorealistic" && (
              <PhotorealisticTab projectId={projectId}
                                  subType={project?.sub_type} />
            )}
            {tab === "model" && (
              <>
                <BackToStageBar tabKey="model" onBack={(stageCode) => { try { sessionStorage.setItem("rachana.stage.activate", stageCode); } catch {} setTab("stages"); }} />
                {project?.domain === "infrastructure" ? (
                  <InfraModel3DViewer projectId={projectId}
                                       subType={project?.sub_type} />
                ) : (
                  <Model3DEditor
                    src={gltfUrl}
                    focusTag={focus3D}
                    projectId={projectId}
                    runId={current?.id}
                    layout={projectModel?.layout || {}}
                    onSaved={async () => {
                      try {
                        const r = await api.get(`/projects/${projectId}/model3d`);
                        setProjectModel(r.data);
                      } catch (e) { console.warn("Model reload failed:", e?.message); }
                    }}
                  />
                )}
              </>
            )}
            {/* Sprint 3 Final — highway master plan moved INSIDE A.5.1 stage gate */}
            {false && tab === "highway-master-plan" && (
              <HighwayMasterPlanPanel projectId={projectId} />
            )}
            {tab === "clashes" && <ClashesTab run={current} projectId={projectId} onFocusClash={focusOnClash} />}
            {tab === "tags" && <TagsTab tags={tags} onOpenTag={openTagDetails} />}
            {tab === "imports" && <ImportsTab projectId={projectId}/>}
            {tab === "symbols" && <SymbolLibrary />}
            {tab === "validation" && (
              <ValidationTab
                issues={current?.validation || []}
                revalidating={revalidating}
                onRevalidate={current?.id ? async () => {
                  try {
                    setRevalidating(true);
                    await api.post(`/projects/${projectId}/runs/${current.id}/revalidate`);
                    const { data } = await api.get(`/projects/${projectId}/runs/${current.id}`);
                    setCurrent(data);
                  } finally {
                    setRevalidating(false);
                  }
                } : undefined}
              />
            )}
            {tab === "collab" && <CollabTab projectId={projectId} runId={current?.id} />}
            {tab === "files" && <FilesTab run={current} gltfUrl={gltfUrl} projectId={projectId} />}
            {tab === "ce_matrix" && <CEMatrixEditor projectId={projectId} />}
            {tab === "plot_plan" && <PlotPlanEditor projectId={projectId} />}
            {tab === "hazop" && <HazopWorksheet projectId={projectId} />}
            {tab === "sil_lopa" && <SilLopaWorksheet projectId={projectId} />}
            {tab === "disciplines" && <DisciplineHub projectId={projectId} phaseVisibility={phaseVisibility} />}
            {tab === "loops" && <LoopsPanel projectId={projectId} />}
          </div>
        </section>
      </div>
      </div>

      {sendModal && (
        <SendHandoverModal
          projectId={projectId}
          scope={sendModal.scope}
          stageCode={sendModal.stageCode}
          defaultLabel={sendModal.label}
          onClose={() => setSendModal(null)}
        />
      )}

      {walkthroughOpen && tab === "stages" && (
        <StageWalkthrough
          catalogue={gate.data?.catalogue || []}
          activeCode={stageCode}
          onPick={(code) => setStageCode(code)}
          onClose={() => setWalkthroughOpen(false)}
        />
      )}

      {ctxMenu && (
        <TagContextMenu
          tag={ctxMenu.tag}
          x={ctxMenu.x}
          y={ctxMenu.y}
          onExplore={() => openTagDetails(ctxMenu.tag)}
          onCopy={() => { navigator.clipboard?.writeText(ctxMenu.tag); setCtxMenu(null); }}
          onClose={() => setCtxMenu(null)}
        />
      )}

      {openTag && (
        <TagDetailsPanel
          projectId={projectId}
          tag={openTag}
          onClose={closeTagDetails}
          hasPosition3D={!!(current?.equipment_positions || {})[openTag]}
          onLocate3D={() => locateInThreeD(openTag)}
        />
      )}
    </AppShell>
  );
}

function EngineStatus({ run }) {
  const lines = [
    { k: "L1", label: "Interpret prompt → PDO JSON", state: run.layers?.l1?.status },
    { k: "L2", label: "Generate Process Flow Diagram", state: run.layers?.l2?.status },
    { k: "L3", label: "Enrich with instruments → P&ID", state: run.layers?.l3?.status },
    { k: "L4", label: "Compose 3D plant layout (glTF)", state: run.layers?.l4?.status },
  ];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {lines.map((l) => (
        <div key={l.k} className="pf-mono" style={{ fontSize: 12, color: "var(--pf-text)" }}>
          <span style={{ color: "var(--pf-text-muted)" }}>[{l.k}]</span>{" "}
          <span style={{ color: l.state === "complete" ? "var(--pf-ok)" : l.state === "running" ? "var(--pf-accent)" : l.state === "failed" ? "var(--pf-err)" : "var(--pf-text-dim)" }}>
            {(l.state || "pending").toUpperCase()}
          </span>{" "}
          — {l.label}
        </div>
      ))}
    </div>
  );
}

function AssetsUsed({ assets }) {
  if (!assets) return null;
  const chips = [];
  if (assets.document?.used) chips.push({ lbl: "DOC", v: assets.document.filename });
  if (assets.symbol_library?.used) chips.push({ lbl: "LIB", v: assets.symbol_library.name });
  if (assets.template_pfd?.used) chips.push({ lbl: "TPL PFD", v: assets.template_pfd.name });
  if (assets.template_pid?.used) chips.push({ lbl: "TPL P&ID", v: assets.template_pid.name });
  if (!chips.length) return null;
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10 }}>
      {chips.map((c, i) => (
        <span key={i} className="pf-ind-chip" style={{ color: "var(--pf-accent)", borderColor: "var(--pf-accent)" }}>
          {c.lbl}: {c.v}
        </span>
      ))}
    </div>
  );
}

const THOUGHT_ICONS = {
  info: "·",
  layer: "▸",
  validation: "⚑",
  warn: "!",
  error: "✕",
  done: "✓",
};

const THOUGHT_COLORS = {
  info: "var(--pf-text-muted)",
  layer: "var(--pf-accent)",
  validation: "var(--pf-warn)",
  warn: "var(--pf-warn)",
  error: "var(--pf-err)",
  done: "var(--pf-ok)",
};

function ThoughtStream({ thoughts, running, endRef }) {
  if (!thoughts || thoughts.length === 0) {
    if (!running) return null;
    return (
      <div style={{ marginTop: 10, color: "var(--pf-text-dim)", fontSize: 12 }} className="pf-mono" data-testid="thought-stream-empty">
        <span className="pf-spin" style={{ display: "inline-block", marginRight: 6 }}/> Waiting for engine stream…
      </div>
    );
  }
  return (
    <div style={{ marginTop: 10, borderTop: "1px dashed var(--pf-border-muted)", paddingTop: 10 }} data-testid="thought-stream">
      <div className="pf-overline" style={{ fontSize: 9, marginBottom: 6, color: "var(--pf-text-dim)" }}>LIVE THOUGHTS · {thoughts.length}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 260, overflowY: "auto" }}>
        {thoughts.map((t, i) => (
          <div
            key={i}
            className="pf-mono"
            style={{ fontSize: 12, color: THOUGHT_COLORS[t.kind] || "var(--pf-text-muted)", lineHeight: 1.45 }}
            data-testid={`thought-${i}`}
          >
            <span style={{ width: 14, display: "inline-block", opacity: 0.7 }}>{THOUGHT_ICONS[t.kind] || "·"}</span>
            {t.message}
          </div>
        ))}
        <div ref={endRef} />
      </div>
    </div>
  );
}


function OverviewTab({ run, pdo, onOpenTag }) {
  if (!run) {
    return (
      <div className="pf-card pf-blueprint-bg" style={{ padding: 48, textAlign: "center" }}>
        <div className="pf-overline" style={{ marginBottom: 8 }}>WORKBENCH · EMPTY</div>
        <h3 className="pf-heading" style={{ fontSize: 22, margin: "4px 0 6px" }}>Start with a prompt</h3>
        <p style={{ color: "var(--pf-text-muted)" }}>Describe your plant on the left and hit <b>Run pipeline</b> (⌘↵). Outputs appear in these tabs as each layer completes.</p>
      </div>
    );
  }
  const meta = pdo?.meta || {};
  const eqCount = pdo?.equipment?.length || 0;
  const stCount = pdo?.streams?.length || 0;
  const insCount = pdo?.instruments?.length || 0;
  const loopCount = pdo?.control_loops?.length || 0;
  const errCount = (run.validation || []).filter((v) => v.severity === "error").length;
  const warnCount = (run.validation || []).filter((v) => v.severity === "warning").length;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }} data-testid="overview-tab">
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
        <Stat label="STATUS" value={run.status?.toUpperCase()} color={run.status === "complete" ? "var(--pf-ok)" : run.status === "failed" ? "var(--pf-err)" : "var(--pf-accent)"} />
        <Stat label="EQUIPMENT" value={eqCount} />
        <Stat label="STREAMS" value={stCount} />
        <Stat label="INSTRUMENTS" value={insCount} />
        <Stat label="CONTROL LOOPS" value={loopCount} />
        <Stat label="VALIDATION" value={`${errCount}E · ${warnCount}W`} color={errCount ? "var(--pf-err)" : warnCount ? "var(--pf-warn)" : "var(--pf-ok)"} />
      </div>

      {pdo && (
        <div className="pf-card" style={{ padding: 16 }}>
          <div className="pf-overline" style={{ marginBottom: 8 }}>DESIGN BASIS</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <Meta k="Project" v={meta.project_name} />
            <Meta k="Industry" v={meta.industry} />
            <Meta k="Capacity" v={meta.capacity} />
            <Meta k="Units" v={meta.units} />
            <Meta k="Standards" v={(meta.standards || []).join(", ")} />
            <Meta k="Basis" v={meta.basis} />
          </div>
          {pdo.control_philosophy && (
            <>
              <div className="pf-overline" style={{ marginTop: 16, marginBottom: 6 }}>CONTROL PHILOSOPHY</div>
              <div style={{ color: "var(--pf-text-muted)", fontSize: 13.5, lineHeight: 1.6 }}>{pdo.control_philosophy}</div>
            </>
          )}
          {pdo.assumptions?.length > 0 && (
            <>
              <div className="pf-overline" style={{ marginTop: 16, marginBottom: 6 }}>ASSUMPTIONS</div>
              <ul style={{ margin: 0, paddingLeft: 18, color: "var(--pf-text-muted)" }}>
                {pdo.assumptions.map((a, i) => <li key={i} style={{ marginBottom: 4 }}>{a}</li>)}
              </ul>
            </>
          )}
        </div>
      )}

      {pdo?.equipment?.length > 0 && (
        <div className="pf-card" style={{ padding: 0, overflow: "hidden" }}>
          <div className="pf-overline" style={{ padding: "10px 16px", borderBottom: "1px solid var(--pf-border)" }}>EQUIPMENT ({pdo.equipment.length})</div>
          <div style={{ overflowX: "auto" }}>
            <table className="pf-mono" style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ background: "var(--pf-surface-2)", color: "var(--pf-text-muted)" }}>
                  {["ID", "TAG", "TYPE", "NAME", "DESIGN T/P", "MATERIAL"].map((h) => (
                    <th key={h} style={{ textAlign: "left", padding: "8px 12px", borderBottom: "1px solid var(--pf-border)" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pdo.equipment.map((e) => (
                  <tr
                    key={e.id}
                    onContextMenu={(ev) => { ev.preventDefault(); onOpenTag?.(e.tag || e.id); }}
                    onClick={() => onOpenTag?.(e.tag || e.id)}
                    style={{ borderBottom: "1px solid var(--pf-border-muted)", cursor: "pointer", transition: "background 0.15s ease" }}
                    onMouseEnter={(ev) => (ev.currentTarget.style.background = "rgba(255,176,0,0.05)")}
                    onMouseLeave={(ev) => (ev.currentTarget.style.background = "transparent")}
                    data-testid={`overview-eq-row-${e.id}`}
                  >
                    <td style={{ padding: "8px 12px", color: "var(--pf-accent)" }}>{e.id}</td>
                    <td style={{ padding: "8px 12px" }}>{e.tag}</td>
                    <td style={{ padding: "8px 12px", color: "var(--pf-accent-2)" }}>{e.type}</td>
                    <td style={{ padding: "8px 12px" }}>{e.name}</td>
                    <td style={{ padding: "8px 12px" }}>{e.design_temp_c ?? "-"} °C / {e.design_pressure_bar ?? "-"} bar</td>
                    <td style={{ padding: "8px 12px" }}>{e.material || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="pf-card" style={{ padding: 12 }}>
      <div className="pf-overline" style={{ fontSize: 9 }}>{label}</div>
      <div className="pf-heading" style={{ fontSize: 22, margin: "4px 0 0", color: color || "var(--pf-text)" }}>{value ?? "—"}</div>
    </div>
  );
}

function Meta({ k, v }) {
  return (
    <div>
      <div className="pf-overline" style={{ fontSize: 9 }}>{k.toUpperCase()}</div>
      <div className="pf-mono" style={{ fontSize: 13, color: "var(--pf-text)", marginTop: 2 }}>{v || "—"}</div>
    </div>
  );
}

function PdoTab({ pdo }) {
  if (!pdo) return <DiagramCanvas svg={null} title="PDO" emptyHint="The structured Process Design Object will appear here once Layer 1 completes." />;
  const str = JSON.stringify(pdo, null, 2);
  return (
    <div style={{ position: "relative" }} data-testid="pdo-tab">
      <button className="btn btn-ghost" style={{ position: "absolute", top: 8, right: 8, zIndex: 2 }}
        onClick={() => {
          const blob = new Blob([str], { type: "application/json" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a"); a.href = url; a.download = "PDO.json"; a.click();
          URL.revokeObjectURL(url);
        }}
        data-testid="pdo-download"
      ><Download size={14}/> PDO.json</button>
      <pre className="pf-code" style={{ maxHeight: "calc(100vh - 280px)" }}>{str}</pre>
    </div>
  );
}

function ValidationTab({ issues, onRevalidate, revalidating }) {
  const [filter, setFilter] = useState("all");
  const categories = ["all", ...Array.from(new Set(issues.map((i) => i.category || "General"))).sort()];
  const filtered = issues.filter((i) => filter === "all" || (i.category || "General") === filter);
  const grouped = { error: [], warning: [], info: [] };
  filtered.forEach((i) => (grouped[i.severity] || grouped.info).push(i));
  const counts = issues.reduce((a, i) => {
    a[i.severity] = (a[i.severity] || 0) + 1;
    return a;
  }, {});
  if (!issues.length) {
    return <div className="pf-card pf-blueprint-bg" style={{ padding: 48, textAlign: "center", color: "var(--pf-text-muted)" }}>
      <div className="pf-overline" style={{ marginBottom: 8 }}>VALIDATION</div>
      No issues recorded yet.
      {onRevalidate && (
        <div style={{ marginTop: 16 }}>
          <button className="btn btn-primary" onClick={onRevalidate} disabled={revalidating} data-testid="btn-revalidate-empty">
            {revalidating ? "Running…" : "Run v3 Validation Engine"}
          </button>
        </div>
      )}
    </div>;
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }} data-testid="validation-tab">
      {/* Summary stat row */}
      <div className="pf-card" style={{ padding: "12px 14px", display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
        <div className="pf-overline">SUMMARY</div>
        <div style={{ display: "flex", gap: 10 }}>
          <span className="pf-sev pf-sev-error">{counts.error || 0} errors</span>
          <span className="pf-sev pf-sev-warning">{counts.warning || 0} warnings</span>
          <span className="pf-sev pf-sev-info">{counts.info || 0} info</span>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`validation-filter-${c.replace(/\s+/g, "-").toLowerCase()}`}
              onClick={() => setFilter(c)}
              className={filter === c ? "btn btn-primary" : "btn btn-ghost"}
              style={{ fontSize: 11, padding: "4px 10px" }}
            >
              {c}
            </button>
          ))}
          {onRevalidate && (
            <button className="btn btn-ghost" onClick={onRevalidate} disabled={revalidating} style={{ fontSize: 11 }} data-testid="btn-revalidate">
              {revalidating ? "…" : "Re-run Engine"}
            </button>
          )}
        </div>
      </div>
      {["error", "warning", "info"].map((sev) => grouped[sev].length > 0 && (
        <div key={sev} className="pf-card" style={{ padding: 0, overflow: "hidden" }}>
          <div className="pf-overline" style={{ padding: "10px 14px", borderBottom: "1px solid var(--pf-border)" }}>{sev.toUpperCase()} ({grouped[sev].length})</div>
          <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {grouped[sev].map((it, i) => (
              <li key={i} data-testid={`validation-issue-${it.code}`} style={{ padding: "10px 14px", borderBottom: "1px solid var(--pf-border-muted)", display: "flex", gap: 10, alignItems: "flex-start" }}>
                <span className={`pf-sev pf-sev-${sev}`}>{sev}</span>
                <span className="pf-mono" style={{ fontSize: 11, color: "var(--pf-text-muted)", minWidth: 72 }}>[{it.code}]</span>
                {it.category && <span className="pf-ind-chip" style={{ fontSize: 9 }}>{it.category}</span>}
                <span style={{ flex: 1, color: "var(--pf-text)" }}>
                  {it.message}
                  {it.refs && it.refs.length > 0 && (
                    <span style={{ display: "block", marginTop: 3, fontSize: 10, color: "var(--pf-text-muted)" }}>
                      Refs: {it.refs.join(" · ")}
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function FilesTab({ run, gltfUrl, projectId: projectIdProp }) {
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  const [designReports, setDesignReports] = useState([]);
  const projectId = projectIdProp || run?.project_id;

  useEffect(() => {
    if (!projectId) return;
    let alive = true;
    api.get(`/projects/${projectId}/infra-re/design-reports`)
      .then(({ data }) => { if (alive) setDesignReports(data || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [projectId]);

  if (!run && designReports.length === 0) {
    return <div className="pf-card" style={{ padding: 32, color: "var(--pf-text-muted)" }}>No outputs yet.</div>;
  }

  /** Authenticated download: fetches the URL with credentials, turns the
   *  response into a Blob, then triggers a local `<a download>` click. This
   *  is necessary because cross-origin `<a href>` links ignore the `download`
   *  attribute AND browsers increasingly block 3rd-party cookies, which
   *  means naive anchor links silently fail on our API's cookie auth.
   *
   *  `dataUri` is the escape hatch for pure-client artefacts (PDO.json) that
   *  don't need a roundtrip.
   */
  const download = async (name, url, dataUri) => {
    setErr(null);
    try {
      setBusy(name);
      let blob;
      if (dataUri) {
        const resp = await fetch(dataUri);
        blob = await resp.blob();
      } else {
        const resp = await fetch(url, { credentials: "include" });
        if (!resp.ok) throw new Error(`HTTP ${resp.status} — ${await resp.text().catch(() => "")}`);
        blob = await resp.blob();
      }
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(href), 4000);
    } catch (e) {
      setErr(`${name}: ${e.message || e}`);
    } finally {
      setBusy(null);
    }
  };

  const pdo = run?.pdo_enriched || run?.pdo;
  const items = [
    run && pdo && {
      name: "PDO.json",
      dataUri: `data:application/json;charset=utf-8,${encodeURIComponent(JSON.stringify(pdo, null, 2))}`,
      size: `${new Blob([JSON.stringify(pdo)]).size} B · Machine-readable source-of-truth`,
      testid: "file-pdo",
      badge: "JSON",
    },
    run?.pfd_svg && { name: "PFD.svg", url: `${API_BASE}/runs/${run.id}/pfd.svg`, size: `${new Blob([run.pfd_svg || ""]).size} B · Web`, testid: "file-pfd" },
    run?.pid_svg && { name: "PID.svg", url: `${API_BASE}/runs/${run.id}/pid.svg`, size: `${new Blob([run.pid_svg || ""]).size} B · Web`, testid: "file-pid" },
    run?.status === "complete" && run.pid_sheets && run.pid_sheets.length > 0 && projectId && {
      name: "PID_Book.zip",
      url: `${API_BASE}/projects/${projectId}/runs/${run.id}/pid_book.zip`,
      size: `${run.pid_sheets.length} sheets · cover index + per-unit SVG/DXF · ISA-5.1 OPCs`,
      testid: "file-pid-book",
      badge: "P&ID BOOK",
    },
    gltfUrl && { name: "plant.glb", url: `${API_BASE}/runs/${run.id}/model.gltf`, size: "glTF 2.0 · Revit / Navisworks / web", testid: "file-gltf", badge: "3D" },
    run?.status === "complete" && projectId && {
      name: "Engineering_Package.xlsx",
      url: `${API_BASE}/projects/${projectId}/runs/${run.id}/datasheet.xlsx`,
      size: "Equipment · Instruments · Line List · Validation",
      testid: "file-xlsx",
    },
    run?.status === "complete" && projectId && {
      name: "Engineering_Package.pdf",
      url: `${API_BASE}/projects/${projectId}/runs/${run.id}/datasheet.pdf`,
      size: "Design basis · Schedules · Validation (A4 landscape)",
      testid: "file-pdf",
    },
    run?.status === "complete" && projectId && {
      name: "MTO.xlsx",
      url: `${API_BASE}/projects/${projectId}/runs/${run.id}/mto.xlsx`,
      size: "Material Take-Off per discipline · weight summary",
      testid: "file-mto",
      badge: "MTO",
    },
    run?.status === "complete" && gltfUrl && projectId && {
      name: "Isometrics.pdf",
      url: `${API_BASE}/projects/${projectId}/runs/${run.id}/isometrics.pdf`,
      size: "Auto-ISO drawings · one page per pipe · A3 landscape · w/ BOM",
      testid: "file-iso",
      badge: "ISO",
    },
    run?.status === "complete" && gltfUrl && projectId && {
      name: "Clash_Report.json",
      url: `${API_BASE}/projects/${projectId}/runs/${run.id}/clashes.json`,
      size: "3-phase clash detection (hard + soft + clearance)",
      testid: "file-clash",
      badge: "CLASH",
    },
    run?.status === "complete" && gltfUrl && projectId && {
      name: "Clashes.bcfzip",
      url: `${API_BASE}/projects/${projectId}/runs/${run.id}/clashes.bcf`,
      size: "BCF 3.0 · Navisworks / Solibri / BIMcollab / Revit BCF Manager",
      testid: "file-bcf",
      badge: "BCF 3.0",
    },
    run?.status === "complete" && run.pfd_svg && projectId && {
      name: "PFD.dxf",
      url: `${API_BASE}/projects/${projectId}/runs/${run.id}/pfd.dxf`,
      size: "AutoCAD R2018 · Opens in AutoCAD, AVEVA, SmartPlant, Bentley",
      testid: "file-pfd-dxf",
      badge: "DXF",
    },
    run?.status === "complete" && run.pid_svg && projectId && {
      name: "PID.dxf",
      url: `${API_BASE}/projects/${projectId}/runs/${run.id}/pid.dxf`,
      size: "AutoCAD R2018 · Layered (Equipment/Instruments/Lines/Signals)",
      testid: "file-pid-dxf",
      badge: "DXF",
    },
    run?.status === "complete" && run.pfd_svg && projectId && {
      name: "PFD.dwg",
      url: `${API_BASE}/projects/${projectId}/runs/${run.id}/pfd.dwg`,
      size: "AutoCAD DWG · Native drag-and-drop into AutoCAD",
      testid: "file-pfd-dwg",
      badge: "DWG",
    },
    run?.status === "complete" && run.pid_svg && projectId && {
      name: "PID.dwg",
      url: `${API_BASE}/projects/${projectId}/runs/${run.id}/pid.dwg`,
      size: "AutoCAD DWG · Autodesk-compatible",
      testid: "file-pid-dwg",
      badge: "DWG",
    },
    run?.status === "complete" && run.pfd_svg && projectId && {
      name: "PFD_AutoCAD_Template.dwg",
      url: `${API_BASE}/projects/${projectId}/runs/${run.id}/pfd_template.dwg`,
      size: "Layered DWG (PROCESSFLOW/EQUIPMENT/INSTRUMENT/UTILITY/ANNOTATION) · opens with native P&ID intelligence",
      testid: "file-pfd-tpl",
      badge: "P&ID TPL",
    },
    run?.status === "complete" && run.pid_svg && projectId && {
      name: "PID_AutoCAD_Template.dwg",
      url: `${API_BASE}/projects/${projectId}/runs/${run.id}/pid_template.dwg`,
      size: "Layered DWG · tag attributes embedded · AutoCAD P&ID / AVEVA P&ID / SmartPlant P&ID ready",
      testid: "file-pid-tpl",
      badge: "P&ID TPL",
    },
    run?.status === "complete" && gltfUrl && projectId && {
      name: "plant.ifc",
      url: `${API_BASE}/projects/${projectId}/runs/${run.id}/model.ifc`,
      size: "IFC 4 · Revit / Tekla / AVEVA E3D / Navisworks",
      testid: "file-ifc",
      badge: "BIM",
    },
    run?.status === "complete" && gltfUrl && projectId && {
      name: "plant.stl",
      url: `${API_BASE}/projects/${projectId}/runs/${run.id}/model.stl`,
      size: "STL · SolidWorks / Inventor / CATIA / Fusion 360",
      testid: "file-stl",
      badge: "CAD",
    },
    run?.status === "complete" && projectId && {
      name: "plant.step",
      url: `${API_BASE}/projects/${projectId}/runs/${run.id}/model.step`,
      size: "STEP AP242 MIM_LF (ISO-10303-242) · parametric assembly from ~100 CadQuery templates",
      testid: "file-step",
      badge: "AP242",
    },
    run?.status === "complete" && projectId && {
      name: "plant_parametric.stl",
      url: `${API_BASE}/projects/${projectId}/runs/${run.id}/model.parametric.stl`,
      size: "Hi-res STL · tessellated directly from AP242 (for 3-D printing / Navisworks)",
      testid: "file-step-stl",
      badge: "HD STL",
    },
    run?.status === "complete" && projectId && {
      name: "plant.dae",
      url: `${API_BASE}/projects/${projectId}/runs/${run.id}/model.dae`,
      size: "COLLADA 1.4 · native to Navisworks / SketchUp / Maya / Blender",
      testid: "file-dae",
      badge: "COLLADA",
    },
    run?.status === "complete" && projectId && {
      name: "plant.obj",
      url: `${API_BASE}/projects/${projectId}/runs/${run.id}/model.obj`,
      size: "Wavefront OBJ · universal mesh · Unity / Unreal / MeshLab",
      testid: "file-obj",
      badge: "OBJ",
    },
    run?.status === "complete" && projectId && {
      name: "Navisworks_Coordination_Bundle.zip",
      url: `${API_BASE}/projects/${projectId}/runs/${run.id}/navisworks_bundle.zip`,
      size: "IFC + STEP + DAE + OBJ + STL + GLB + DWG + convert_to_nwc.bat · drag-drop into Navisworks",
      testid: "file-navisworks",
      badge: "NAVISWORKS",
    },
    run?.status === "complete" && run.pid_svg && projectId && {
      name: "PID.dexpi.xml",
      url: `${API_BASE}/projects/${projectId}/runs/${run.id}/pid.dexpi.xml`,
      size: "DEXPI 1.3 · AVEVA / SmartPlant P&ID / Bentley OpenPlant",
      testid: "file-dexpi",
      badge: "ISO 15926",
    },
    run?.status === "complete" && projectId && {
      name: "IFC_Package.zip",
      url: `${API_BASE}/projects/${projectId}/runs/${run.id}/package.zip`,
      size: "Full EPC handover · all 15 artefacts + manifest.json",
      testid: "file-zip",
      badge: "DELIVERABLE",
      primary: true,
    },
    ...designReports.map((rep) => ({
      name: rep.filename,
      url: `${API_BASE}/projects/${projectId}/stages/${rep.stage_code}/attachments/${rep.id}/download`,
      size: `${(rep.size_bytes/1024).toFixed(1)} KB · Saved ${new Date(rep.uploaded_at).toLocaleString()} · ${rep.domain === "real_estate" ? "Real-Estate" : "Infrastructure"} composite run`,
      testid: `file-design-report-${rep.id.slice(0,8)}`,
      badge: "DESIGN REPORT",
    })),
  ].filter(Boolean);

  return (
    <div className="pf-card" data-testid="files-tab">
      <div className="pf-overline" style={{ padding: "10px 14px", borderBottom: "1px solid var(--pf-border)" }}>
        DOWNLOADS · {items.length} ARTEFACTS · importable into Autodesk, AVEVA, Hexagon SmartPlant, Bentley
      </div>
      {err && (
        <div className="pf-mono" style={{ padding: 10, background: "rgba(255,60,60,0.08)", color: "var(--pf-err)", fontSize: 12 }} data-testid="files-error">
          {err}
        </div>
      )}
      {items.length === 0 ? (
        <div style={{ padding: 24, color: "var(--pf-text-muted)" }}>No files yet — run the pipeline to generate outputs.</div>
      ) : (
        <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {items.map((it) => (
            <li key={it.name} style={{ padding: "12px 16px", borderBottom: "1px solid var(--pf-border-muted)", display: "flex", alignItems: "center", justifyContent: "space-between", background: it.primary ? "rgba(255,176,0,0.06)" : "transparent" }} data-testid={it.testid}>
              <div>
                <div className="pf-mono" style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}>
                  {it.name}
                  {it.badge && <span className="pf-ind-chip" style={{ color: it.primary ? "var(--pf-accent)" : "var(--pf-text-muted)", borderColor: it.primary ? "var(--pf-accent)" : "var(--pf-border)", fontSize: 9 }}>{it.badge}</span>}
                </div>
                <div className="pf-mono" style={{ fontSize: 10, color: "var(--pf-text-muted)" }}>{it.size}</div>
              </div>
              <button
                className={it.primary ? "btn btn-primary" : "btn btn-ghost"}
                onClick={() => download(it.name, it.url, it.dataUri)}
                disabled={busy === it.name}
                data-testid={`${it.testid}-download`}
              >
                {busy === it.name ? (<><span className="pf-spin" style={{ marginRight: 6 }}/> Downloading…</>) : (<><Download size={14}/> Download</>)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


function TagsTab({ tags, onOpenTag }) {
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all"); // all | equipment | instrument | stream
  if (!tags || tags.length === 0) {
    return (
      <div className="pf-card pf-blueprint-bg" style={{ padding: 48, textAlign: "center", color: "var(--pf-text-muted)" }}>
        <div className="pf-overline" style={{ marginBottom: 8 }}>TAG DATABASE · EMPTY</div>
        The tag database is populated automatically after a pipeline run. It links every equipment, instrument and stream to its engineering datasheet.
      </div>
    );
  }
  const filtered = tags.filter((t) => {
    if (filter !== "all" && t.klass !== filter) return false;
    if (!q) return true;
    const hay = (t.tag + " " + (t.name || "") + " " + (t.subtype || "")).toLowerCase();
    return hay.includes(q.toLowerCase());
  });
  const byClass = (k) => tags.filter((t) => t.klass === k).length;
  return (
    <div data-testid="tags-tab">
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
        <input className="pf-input" style={{ maxWidth: 360 }} placeholder="Search tags…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="tags-search" />
        {[
          { k: "all", lbl: `ALL (${tags.length})` },
          { k: "equipment", lbl: `EQUIPMENT (${byClass("equipment")})` },
          { k: "instrument", lbl: `INSTRUMENTS (${byClass("instrument")})` },
          { k: "stream", lbl: `STREAMS (${byClass("stream")})` },
        ].map((f) => (
          <button
            key={f.k}
            className="btn btn-ghost"
            onClick={() => setFilter(f.k)}
            style={{ borderColor: filter === f.k ? "var(--pf-accent)" : "var(--pf-border)", color: filter === f.k ? "var(--pf-accent)" : "var(--pf-text)" }}
            data-testid={`tags-filter-${f.k}`}
          >
            {f.lbl}
          </button>
        ))}
      </div>
      <div className="pf-card" style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table className="pf-mono" style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "var(--pf-surface-2)", color: "var(--pf-text-muted)" }}>
                {["TAG", "CLASS", "TYPE", "NAME", "KEY PROPERTIES", "STATUS"].map((h) => (
                  <th key={h} style={{ textAlign: "left", padding: "10px 12px", borderBottom: "1px solid var(--pf-border)" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => {
                const p = t.properties || {};
                let keys = "—";
                if (t.klass === "equipment") {
                  keys = `${p.design_temperature_c ?? "-"}°C / ${p.design_pressure_bar ?? "-"} bar · ${p.material_of_construction || "-"}`;
                } else if (t.klass === "instrument") {
                  keys = `${p.measurement_variable || ""} ${p.range_low ?? "-"}–${p.range_high ?? "-"} ${p.range_units || ""}`;
                } else if (t.klass === "stream") {
                  keys = `${p.flow_kg_h ?? "-"} kg/h @ ${p.temperature_c ?? "-"}°C / ${p.pressure_bar ?? "-"} bar`;
                }
                return (
                  <tr
                    key={t.tag}
                    onClick={() => onOpenTag?.(t.tag)}
                    onContextMenu={(ev) => { ev.preventDefault(); onOpenTag?.(t.tag); }}
                    style={{ borderBottom: "1px solid var(--pf-border-muted)", cursor: "pointer", transition: "background .15s ease" }}
                    onMouseEnter={(ev) => (ev.currentTarget.style.background = "rgba(255,176,0,0.05)")}
                    onMouseLeave={(ev) => (ev.currentTarget.style.background = "transparent")}
                    data-testid={`tagdb-row-${t.tag}`}
                  >
                    <td style={{ padding: "10px 12px", color: "var(--pf-accent)" }}>{t.tag}</td>
                    <td style={{ padding: "10px 12px", color: "var(--pf-text-muted)" }}>{t.klass}</td>
                    <td style={{ padding: "10px 12px", color: "var(--pf-accent-2)" }}>{t.subtype}</td>
                    <td style={{ padding: "10px 12px" }}>{t.name}</td>
                    <td style={{ padding: "10px 12px", color: "var(--pf-text-muted)" }}>{keys}</td>
                    <td style={{ padding: "10px 12px" }}>{p.status || "Design"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <div className="pf-mono" style={{ marginTop: 10, fontSize: 10, color: "var(--pf-text-dim)" }}>
        Tip: click any row to open the full engineering datasheet. Right-click any tag in the PFD or P&amp;ID for the same menu.
      </div>
    </div>
  );
}


// ---------- 3D Clash Inspector Tab ----------
const CLASH_SEVERITIES = {
  CRITICAL: { label: "Critical", color: "#FF2E2E", bg: "rgba(255,46,46,0.10)" },
  HIGH:     { label: "High",     color: "#FFB000", bg: "rgba(255,176,0,0.10)" },
  MEDIUM:   { label: "Medium",   color: "#FFD56B", bg: "rgba(255,213,107,0.10)" },
  LOW:      { label: "Low",      color: "#9AA4B2", bg: "rgba(154,164,178,0.10)" },
};

function ClashesTab({ run, projectId, onFocusClash }) {
  const [filterSev, setFilterSev] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [exporting, setExporting] = useState(false);
  const [err, setErr] = useState(null);
  const clashes = (run?.layout_3d || {}).clashes || [];

  const filtered = useMemo(() => clashes.filter((c) => {
    if (filterSev !== "all" && (c.severity || "").toUpperCase() !== filterSev) return false;
    if (filterType !== "all" && (c.type || "") !== filterType) return false;
    return true;
  }), [clashes, filterSev, filterType]);

  const counts = useMemo(() => {
    const out = { total: clashes.length, hard: 0, soft: 0, clearance: 0, CRITICAL: 0, HIGH: 0, MEDIUM: 0, LOW: 0 };
    clashes.forEach((c) => {
      out[c.type] = (out[c.type] || 0) + 1;
      out[(c.severity || "").toUpperCase()] = (out[(c.severity || "").toUpperCase()] || 0) + 1;
    });
    return out;
  }, [clashes]);

  const exportBCF = async () => {
    if (!projectId || !run?.id) return;
    setErr(null); setExporting(true);
    try {
      const resp = await fetch(`${API_BASE}/projects/${projectId}/runs/${run.id}/clashes.bcf`, { credentials: "include" });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = `clashes_${run.id.slice(0, 8)}.bcfzip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(href), 4000);
    } catch (e) {
      setErr(`BCF export failed: ${e.message || e}`);
    } finally {
      setExporting(false);
    }
  };

  if (!run) {
    return <div className="pf-card" style={{ padding: 32, color: "var(--pf-text-muted)" }}>Run the pipeline to detect clashes.</div>;
  }

  return (
    <div className="pf-card" data-testid="clash-inspector">
      {/* Summary strip */}
      <div style={{ padding: "14px 16px", borderBottom: "1px solid var(--pf-border)", display: "flex", gap: 18, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <AlertTriangle size={18} color="#FFB000"/>
          <div className="pf-overline" style={{ color: "var(--pf-text)" }}>3D CLASH INSPECTOR</div>
        </div>
        <div className="pf-mono" style={{ fontSize: 13, display: "flex", gap: 16 }} data-testid="clash-counts">
          <span><b style={{ color: "#FF2E2E" }}>{counts.CRITICAL || 0}</b> critical</span>
          <span><b style={{ color: "#FFB000" }}>{counts.HIGH || 0}</b> high</span>
          <span><b style={{ color: "#FFD56B" }}>{counts.MEDIUM || 0}</b> medium</span>
          <span><b style={{ color: "var(--pf-text-dim)" }}>{counts.total}</b> total</span>
          <span style={{ color: "var(--pf-text-dim)" }}>|</span>
          <span><b style={{ color: "#FF2E2E" }}>{counts.hard || 0}</b> hard</span>
          <span><b style={{ color: "#FFB000" }}>{counts.soft || 0}</b> soft</span>
          <span><b style={{ color: "var(--pf-text-dim)" }}>{counts.clearance || 0}</b> clearance</span>
        </div>
        <div style={{ flex: 1 }}/>
        <button className="btn btn-ghost" onClick={exportBCF} disabled={exporting || !clashes.length} data-testid="clash-export-bcf">
          {exporting ? (<><span className="pf-spin" style={{ marginRight: 6 }}/> Building…</>) : (<><Download size={14}/> Export BCF 3.0</>)}
        </button>
      </div>

      {/* Filter row */}
      <div style={{ padding: "10px 16px", borderBottom: "1px solid var(--pf-border-muted)", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <span className="pf-mono" style={{ fontSize: 10, color: "var(--pf-text-dim)" }}>SEVERITY</span>
        {["all", "CRITICAL", "HIGH", "MEDIUM", "LOW"].map((s) => (
          <button
            key={s}
            className={`btn btn-ghost`}
            onClick={() => setFilterSev(s)}
            style={{ padding: "4px 10px", fontSize: 11, borderColor: filterSev === s ? "var(--pf-accent)" : "var(--pf-border)", color: filterSev === s ? "var(--pf-accent)" : "var(--pf-text)" }}
            data-testid={`clash-filter-sev-${s}`}
          >{s === "all" ? "All" : CLASH_SEVERITIES[s]?.label || s}</button>
        ))}
        <span className="pf-mono" style={{ fontSize: 10, color: "var(--pf-text-dim)", marginLeft: 20 }}>TYPE</span>
        {["all", "hard", "soft", "clearance"].map((t) => (
          <button
            key={t}
            className={`btn btn-ghost`}
            onClick={() => setFilterType(t)}
            style={{ padding: "4px 10px", fontSize: 11, borderColor: filterType === t ? "var(--pf-accent)" : "var(--pf-border)", color: filterType === t ? "var(--pf-accent)" : "var(--pf-text)" }}
            data-testid={`clash-filter-type-${t}`}
          >{t === "all" ? "All" : t[0].toUpperCase() + t.slice(1)}</button>
        ))}
        <div style={{ flex: 1 }}/>
        <span className="pf-mono" style={{ fontSize: 10, color: "var(--pf-text-dim)" }}>{filtered.length} shown</span>
      </div>

      {err && (
        <div className="pf-mono" style={{ padding: 10, background: "rgba(255,60,60,0.08)", color: "var(--pf-err)", fontSize: 12 }}>{err}</div>
      )}

      {clashes.length === 0 ? (
        <div style={{ padding: 32, color: "var(--pf-text-muted)", textAlign: "center" }} data-testid="clash-empty">
          No clashes detected. The 3-phase engine didn't find any geometric conflicts in this layout — either the plant passes cleanly or the v4 Layer-4 output hasn't produced enough multi-discipline geometry yet. Kick off a fresh run to exercise all 17 disciplines.
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: 24, color: "var(--pf-text-muted)", textAlign: "center" }} data-testid="clash-filtered-empty">No clashes match the current filters.</div>
      ) : (
        <ul style={{ listStyle: "none", margin: 0, padding: 0, maxHeight: 640, overflowY: "auto" }}>
          {filtered.map((c) => {
            const sev = CLASH_SEVERITIES[(c.severity || "HIGH").toUpperCase()] || CLASH_SEVERITIES.HIGH;
            return (
              <li
                key={c.id}
                style={{ padding: "12px 16px", borderBottom: "1px solid var(--pf-border-muted)", display: "flex", alignItems: "center", gap: 14, background: sev.bg }}
                data-testid={`clash-row-${c.id}`}
              >
                <div style={{ minWidth: 68 }}>
                  <span className="pf-mono pf-ind-chip" style={{ color: sev.color, borderColor: sev.color, fontSize: 10 }}>{sev.label}</span>
                </div>
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
                  <div className="pf-mono" style={{ fontSize: 13 }}>
                    <b>{c.id}</b> · {c.type?.toUpperCase()} · <span style={{ color: "var(--pf-accent)" }}>{c.object_1}</span> ↔ <span style={{ color: "var(--pf-accent)" }}>{c.object_2}</span>
                  </div>
                  <div className="pf-mono" style={{ fontSize: 11, color: "var(--pf-text-muted)" }}>{c.description}</div>
                  {c.location && (
                    <div className="pf-mono" style={{ fontSize: 10, color: "var(--pf-text-dim)" }}>
                      @ ({c.location[0]?.toFixed?.(0) ?? "?"} , {c.location[1]?.toFixed?.(0) ?? "?"} , {c.location[2]?.toFixed?.(0) ?? "?"}) mm
                      {c.distance_mm !== undefined && ` · separation ${c.distance_mm.toFixed ? c.distance_mm.toFixed(0) : c.distance_mm} mm`}
                      {c.required_mm !== undefined && ` · required ${c.required_mm.toFixed ? c.required_mm.toFixed(0) : c.required_mm} mm`}
                    </div>
                  )}
                </div>
                <button
                  className="btn btn-ghost"
                  style={{ padding: "6px 12px", fontSize: 11 }}
                  onClick={() => onFocusClash?.(c)}
                  title="Fly camera to this clash in 3D"
                  data-testid={`clash-jump-${c.id}`}
                >
                  <Cuboid size={13}/> Jump to 3D
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}



// ---------- Hazardous-area Tab ----------
function HazareaTab({ projectId, runId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!runId) return;
    setLoading(true);
    api.get(`/projects/${projectId}/runs/${runId}/hazarea`).then(r => setData(r.data)).catch(() => setData({ error: "unavailable" })).finally(() => setLoading(false));
  }, [projectId, runId]);
  if (!runId) return <div className="pf-card" style={{ padding: 32, color: "var(--pf-text-muted)" }}>Select a completed run first.</div>;
  if (loading) return <div className="pf-card" style={{ padding: 32 }}>Loading hazardous-area classification…</div>;
  if (!data || data.error) return <div className="pf-card" style={{ padding: 32 }}>{data?.error || "No data"}</div>;
  const zones = data.zone_counts || {};
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }} data-testid="hazarea-tab">
      <div className="pf-card" style={{ padding: 14 }}>
        <div className="pf-overline">CLASSIFICATION (API RP 500 · IEC 60079-10-1)</div>
        <div style={{ display: "flex", gap: 20, marginTop: 10, flexWrap: "wrap" }}>
          <HazStat label="Worst Gas Group" value={data.worst_gas_group || "—"} />
          <HazStat label="Worst Temp Class" value={data.worst_temp_class || "—"} />
          <HazStat label="Zone 0" value={zones["Zone 0"] || 0} accent />
          <HazStat label="Zone 1" value={zones["Zone 1"] || 0} accent />
          <HazStat label="Zone 2" value={zones["Zone 2"] || 0} />
          <HazStat label="Non-hazardous" value={zones["NH"] || 0} />
        </div>
        <div className="pf-mono" style={{ marginTop: 10, fontSize: 11, color: "var(--pf-text-muted)" }}>{data.notes}</div>
      </div>
      <div className="pf-card" style={{ padding: 0 }}>
        <div className="pf-overline" style={{ padding: "10px 14px", borderBottom: "1px solid var(--pf-border)" }}>PER-EQUIPMENT MAP</div>
        <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {Object.entries(data.equipment_map || {}).map(([eid, v]) => (
            <li key={eid} style={{ padding: "10px 14px", borderBottom: "1px solid var(--pf-border-muted)", display: "flex", gap: 16, alignItems: "center" }}>
              <span className="pf-mono" style={{ minWidth: 100 }}>{eid}</span>
              <span className={`pf-ind-chip ${v.zone && v.zone !== "NH" ? "pf-chip-hot" : ""}`} style={{ minWidth: 68, textAlign: "center" }}>{v.zone}</span>
              <span className="pf-mono" style={{ fontSize: 11, color: "var(--pf-text-muted)", minWidth: 60 }}>{v.gas_group}</span>
              <span className="pf-mono" style={{ fontSize: 11, color: "var(--pf-text-muted)", minWidth: 40 }}>{v.temp_class}</span>
              <span className="pf-mono" style={{ fontSize: 11, color: "var(--pf-text-muted)" }}>extent ≈ {v.extent_m} m · source: {v.source_of_release}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function HazStat({ label, value, accent }) {
  return (
    <div>
      <div className="pf-mono" style={{ fontSize: 10, color: "var(--pf-text-muted)" }}>{label}</div>
      <div className="pf-mono" style={{ fontSize: 22, color: accent ? "var(--pf-accent)" : "var(--pf-text)" }}>{value}</div>
    </div>
  );
}


// ---------- Team / Collab Tab ----------
function CollabTab({ projectId, runId }) {
  const [members, setMembers] = useState([]);
  const [revs, setRevs] = useState([]);
  const [comments, setComments] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [newRole, setNewRole] = useState("viewer");
  const [newComment, setNewComment] = useState("");
  const [revName, setRevName] = useState("");

  const refresh = async () => {
    const [m, r, c] = await Promise.all([
      api.get(`/projects/${projectId}/members`),
      api.get(`/projects/${projectId}/revisions`),
      api.get(`/projects/${projectId}/comments`),
    ]);
    setMembers(m.data || []);
    setRevs(r.data || []);
    setComments(c.data || []);
  };
  useEffect(() => { if (projectId) refresh(); }, [projectId]);

  const addMember = async () => {
    if (!newEmail) return;
    await api.post(`/projects/${projectId}/members`, { email: newEmail, role: newRole });
    setNewEmail(""); setNewRole("viewer"); refresh();
  };
  const removeMember = async (email) => { await api.delete(`/projects/${projectId}/members/${encodeURIComponent(email)}`); refresh(); };
  const createRev = async () => {
    if (!runId || !revName) return;
    await api.post(`/projects/${projectId}/revisions`, { run_id: runId, name: revName, note: "" });
    setRevName(""); refresh();
  };
  const postComment = async () => {
    if (!newComment) return;
    await api.post(`/projects/${projectId}/comments`, { run_id: runId, anchor_type: "run", anchor_ref: runId, body: newComment });
    setNewComment(""); refresh();
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }} data-testid="collab-tab">
      <div className="pf-card" style={{ padding: 0 }}>
        <div className="pf-overline" style={{ padding: "10px 14px", borderBottom: "1px solid var(--pf-border)" }}>TEAM MEMBERS</div>
        <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {members.map((m) => (
            <li key={m.user_email} style={{ padding: "10px 14px", borderBottom: "1px solid var(--pf-border-muted)", display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ flex: 1 }}>
                <div className="pf-mono">{m.user_email}</div>
                <div className="pf-mono" style={{ fontSize: 10, color: "var(--pf-text-muted)" }}>{m.name || "invited · not yet signed up"}</div>
              </span>
              <span className="pf-ind-chip">{m.role}</span>
              {!m.is_owner && (
                <button className="btn btn-ghost" onClick={() => removeMember(m.user_email)} data-testid={`remove-member-${m.user_email}`}>Remove</button>
              )}
            </li>
          ))}
        </ul>
        <div style={{ padding: 12, display: "flex", gap: 8, borderTop: "1px solid var(--pf-border)" }}>
          <input className="pf-input" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="teammate@company.com" data-testid="collab-email" style={{ flex: 1 }} />
          <select className="pf-input" value={newRole} onChange={(e) => setNewRole(e.target.value)} data-testid="collab-role">
            <option value="viewer">Viewer</option>
            <option value="commenter">Commenter</option>
            <option value="editor">Editor</option>
          </select>
          <button className="btn btn-primary" onClick={addMember} data-testid="collab-invite">Invite</button>
        </div>
      </div>

      <div className="pf-card" style={{ padding: 0 }}>
        <div className="pf-overline" style={{ padding: "10px 14px", borderBottom: "1px solid var(--pf-border)" }}>REVISIONS (ISSUED-FOR-CONSTRUCTION SNAPSHOTS)</div>
        {revs.length === 0 && <div style={{ padding: 16, color: "var(--pf-text-muted)" }}>No revisions yet. Freeze the current run as an immutable snapshot for issue-for-construction or design-review handoff.</div>}
        <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {revs.map((r) => (
            <li key={r.id} style={{ padding: "10px 14px", borderBottom: "1px solid var(--pf-border-muted)", display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ flex: 1 }}>
                <div className="pf-mono" style={{ fontSize: 13 }}>{r.name}</div>
                <div className="pf-mono" style={{ fontSize: 10, color: "var(--pf-text-muted)" }}>{r.run_id?.slice(0, 8)} · {new Date(r.created_at).toLocaleString()} · by {r.created_by_email}</div>
              </span>
              <span className="pf-ind-chip">#{r.id.slice(0, 8)}</span>
            </li>
          ))}
        </ul>
        <div style={{ padding: 12, display: "flex", gap: 8, borderTop: "1px solid var(--pf-border)" }}>
          <input className="pf-input" value={revName} onChange={(e) => setRevName(e.target.value)} placeholder="v1.0 · Issue for Construction" data-testid="rev-name" style={{ flex: 1 }} />
          <button className="btn btn-primary" disabled={!runId || !revName} onClick={createRev} data-testid="rev-create">Freeze Revision</button>
        </div>
      </div>

      <div className="pf-card" style={{ padding: 0 }}>
        <div className="pf-overline" style={{ padding: "10px 14px", borderBottom: "1px solid var(--pf-border)" }}>COMMENTS</div>
        {comments.length === 0 && <div style={{ padding: 16, color: "var(--pf-text-muted)" }}>No comments yet.</div>}
        <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {comments.map((c) => (
            <li key={c.id} style={{ padding: "10px 14px", borderBottom: "1px solid var(--pf-border-muted)" }}>
              <div className="pf-mono" style={{ fontSize: 10, color: "var(--pf-text-muted)" }}>
                {c.author_email} · {new Date(c.created_at).toLocaleString()} · {c.anchor_type}{c.anchor_ref ? `:${c.anchor_ref}` : ""}
              </div>
              <div style={{ marginTop: 4 }}>{c.body}</div>
            </li>
          ))}
        </ul>
        <div style={{ padding: 12, display: "flex", gap: 8, borderTop: "1px solid var(--pf-border)" }}>
          <input className="pf-input" value={newComment} onChange={(e) => setNewComment(e.target.value)} placeholder="Leave a comment on this run…" data-testid="comment-body" style={{ flex: 1 }} />
          <button className="btn btn-primary" onClick={postComment} data-testid="comment-post">Post</button>
        </div>
      </div>
    </div>
  );
}
